import { useCallback, useEffect, useMemo, useState, type FC } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Text } from 'components'
import { projects, getProject, projectImages, projectMedia, resolveLang } from 'pages/Portfolio/portfolioCatalog'
import { categoryLabel } from 'pages/Portfolio/utils'

import styles from './PortfolioDetail.module.scss'

const SITE = 'https://www.engineeringgroup.am'
const BRAND = 'ENGINEERING GROUP'

const PortfolioDetail: FC = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const lang = resolveLang(i18n.language)

  const project = getProject(slug)
  const [lightbox, setLightbox] = useState<number | null>(null)

  const index = useMemo(() => projects.findIndex(p => p.slug === slug), [slug])
  const prev = index > 0 ? projects[index - 1] : undefined
  const next = index >= 0 && index < projects.length - 1 ? projects[index + 1] : undefined

  const images = useMemo(() => (project ? projectImages(project) : []), [project])
  const media = useMemo(() => (project ? projectMedia(project) : []), [project])

  const closeLightbox = useCallback(() => setLightbox(null), [])
  const showPrev = useCallback(
    () => setLightbox(i => (i === null ? i : (i - 1 + media.length) % media.length)),
    [media.length]
  )
  const showNext = useCallback(
    () => setLightbox(i => (i === null ? i : (i + 1) % media.length)),
    [media.length]
  )

  useEffect(() => {
    if (lightbox === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox()
      else if (e.key === 'ArrowLeft') showPrev()
      else if (e.key === 'ArrowRight') showNext()
    }
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    
return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
    }
  }, [lightbox, closeLightbox, showPrev, showNext])

  const handleBack = useCallback(() => navigate('/portfolio'), [navigate])
  const handleContact = useCallback(() => navigate('/contact-us'), [navigate])

  if (!project) {
    return (
      <div className={styles.missing}>
        <Text text='portfolioSubtitle' className={styles.missing__text} tagName='h1' />
        <button className={styles.backBtn} onClick={handleBack}>
          <span className={styles.backBtn__arrow} aria-hidden />
          <Text text='portfolio' />
        </button>
      </div>
    )
  }

  const name = project.name[lang] || project.name.am
  const title = project.title?.[lang] || project.title?.am
  const address = project.address?.[lang] || project.address?.am
  const description = project.description?.[lang] || project.description?.am

  const metaTitle = `${name} | ${BRAND}`
  const metaDescription = title || name
  const pageUrl = `${SITE}/portfolio/${project.slug}`

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('home'), item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: t('portfolio'), item: `${SITE}/portfolio` },
      { '@type': 'ListItem', position: 3, name, item: pageUrl },
    ],
  }

  return (
    <div className={styles.page}>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name='description' content={metaDescription} />
        <meta property='og:title' content={metaTitle} />
        <meta property='og:description' content={metaDescription} />
        <meta property='og:url' content={pageUrl} />
        <meta property='og:image' content={images[0]} />
        <script type='application/ld+json'>{JSON.stringify(structuredData)}</script>
      </Helmet>

      <section className={styles.hero}>
        <div className={styles.hero__media} aria-hidden>
          <img src={images[0]} alt='' className={styles.hero__image} />
          <div className={styles.hero__overlay} />
        </div>

        <div className={classNames('container', styles.hero__inner)}>
          <button className={styles.backBtn} onClick={handleBack}>
            <span className={styles.backBtn__arrow} aria-hidden />
            <Text text='portfolio' />
          </button>

          {project.category && (
            <Text text={categoryLabel[project.category]} className={styles.hero__eyebrow} />
          )}
          <h1 className={styles.hero__title}>{name}</h1>
          {title && <p className={styles.hero__lead}>{title}</p>}

          <div className={styles.meta}>
            {address && (
              <div className={styles.meta__item}>
                <Text text='portfolioAddress' className={styles.meta__label} tagName='span' />
                <span className={styles.meta__value}>{address}</span>
              </div>
            )}
            {project.year && (
              <div className={styles.meta__item}>
                <Text text='portfolioYear' className={styles.meta__label} tagName='span' />
                <span className={styles.meta__value}>{project.year}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className={classNames('container', styles.body)}>
        {description && <p className={styles.description}>{description}</p>}

        <div className={styles.gallery}>
          {media.map((item, i) => (
            <button
              key={item.src}
              type='button'
              className={styles.gallery__item}
              onClick={() => setLightbox(i)}
              aria-label={`${name} — ${i + 1}`}
            >
              <img
                src={item.type === 'video' ? item.poster : item.src}
                alt={`${name} ${i + 1}`}
                loading='lazy'
              />
              <span
                className={item.type === 'video' ? styles.gallery__play : styles.gallery__zoom}
                aria-hidden
              />
            </button>
          ))}
        </div>

        <div className={styles.cta}>
          <Text text='ctaTitle' className={styles.cta__title} tagName='h2' />
          <button className={styles.cta__button} onClick={handleContact}>
            <Text text='contactUs' />
            <span className={styles.cta__arrow} aria-hidden />
          </button>
        </div>

        <nav className={styles.pager} aria-label='Portfolio'>
          {prev ? (
            <button className={styles.pager__link} onClick={() => navigate(`/portfolio/${prev.slug}`)}>
              <span className={styles.pager__dir}>
                <span className={classNames(styles.pager__chev, styles.pager__chev_prev)} aria-hidden />
                <Text text='servicePrev' />
              </span>
              <span className={styles.pager__name}>{prev.name[lang] || prev.name.am}</span>
            </button>
          ) : (
            <span />
          )}
          {next ? (
            <button className={classNames(styles.pager__link, styles.pager__link_next)} onClick={() => navigate(`/portfolio/${next.slug}`)}>
              <span className={styles.pager__dir}>
                <Text text='serviceNext' />
                <span className={styles.pager__chev} aria-hidden />
              </span>
              <span className={styles.pager__name}>{next.name[lang] || next.name.am}</span>
            </button>
          ) : (
            <span />
          )}
        </nav>
      </div>

      {lightbox !== null &&
        createPortal(
          <div className={styles.lightbox} onClick={closeLightbox} role='dialog' aria-modal='true'>
            <button className={styles.lightbox__close} onClick={closeLightbox} aria-label='Close'>
              <span aria-hidden />
            </button>
            {media.length > 1 && (
              <button
                className={classNames(styles.lightbox__nav, styles.lightbox__nav_prev)}
                onClick={e => {
                  e.stopPropagation()
                  showPrev()
                }}
                aria-label='Previous'
              >
                <span aria-hidden />
              </button>
            )}
            {media[lightbox].type === 'video' ? (
              <video
                key={media[lightbox].src}
                src={media[lightbox].src}
                poster={media[lightbox].poster}
                className={styles.lightbox__video}
                controls
                autoPlay
                playsInline
                onClick={e => e.stopPropagation()}
              />
            ) : (
              <img
                src={media[lightbox].src}
                alt={`${name} ${lightbox + 1}`}
                className={styles.lightbox__image}
                onClick={e => e.stopPropagation()}
              />
            )}
            {media.length > 1 && (
              <button
                className={classNames(styles.lightbox__nav, styles.lightbox__nav_next)}
                onClick={e => {
                  e.stopPropagation()
                  showNext()
                }}
                aria-label='Next'
              >
                <span aria-hidden />
              </button>
            )}
            <span className={styles.lightbox__counter}>
              {lightbox + 1} / {media.length}
            </span>
          </div>,
          document.body
        )}
    </div>
  )
}

export default PortfolioDetail
